import React, { useState, useEffect } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import axios from "axios";
import {
  Dialog,
  Button,
  Dropdown,
  TextInput,
  CustomCheckBox,
  Image,
} from "hesus-ui-kit";
import ClipLoader from "react-spinners/ClipLoader";
import { useGetGeneralParameters } from "@hooks/useGeneralParams";
import DocumentUpload from "@components/DocumentUpload/DocumentUpload";
import {
  labelStyles,
  inputStyles,
  labelStylesError,
  inputStylesError,
} from "@utils/extra-style";
import {
  HeaderWrapper,
  Title,
  ButtonWrapper,
  FormBody,
  OptionsWrapper,
  FlexRow,
  SectionContainer,
  GridRow,
  ContainerLabel,
  OperationWrapper,
} from "./AddEditProduct.styled";
import closeIcon from "@icons/ic-close-white.svg";

const AddEditProduct = (props) => {
  const { t, id, isEditMode, title, onClose, updateRefeshStatus } = props;
  const generalParams = useGetGeneralParameters();
  const [isProductCreating, setIsProductCreating] = useState(false);
  const [createProductAPIError, setCreateProductAPIError] = useState("");
  const [existingDocuments, setExistingDocuments] = useState([]);
  const productsListByIds = useSelector((state) => state.productsList.byId);
  const selectedProduct = productsListByIds[id];
  const [countryList, setCountryList] = useState([]);
  const [subCategoryList, setSubCategoryList] = useState([]);

  const [jobTypes, setJobTypes] = useState({
    type1: {
      status: selectedProduct?.canEvacuate ?? false,
    },
    type2: {
      status: selectedProduct?.canDelivery ?? false,
    },
  });

  const onRoleChange = (e) => {
    const checkedRoleId = e.target.id;
    const jobValue = e.target.checked;
    if (checkedRoleId === "1") {
      setJobTypes({
        ...jobTypes,
        type1: {
          status: jobValue,
        },
      });
    }
    if (checkedRoleId === "2") {
      setJobTypes({
        ...jobTypes,
        type2: {
          status: jobValue,
        },
      });
    }
  };

  useEffect(() => {
    if (Object.keys(generalParams).length > 0) {
      setCountryList(generalParams.countryList);
      setSubCategoryList(generalParams.subCategoryList);
    }
  }, [generalParams]);

  const [formData, setFormData] = useState({
    name: {
      required: true,
      value: selectedProduct?.name ?? "",
      error: "",
    },
    code: {
      required: true,
      value: selectedProduct?.productCode ?? "",
      error: "",
    },
    country: {
      required: true,
      value: selectedProduct?.country?.id ?? "",
      error: "",
    },
    subCategory: {
      required: true,
      value: selectedProduct?.subCategory?.id ?? "",
      error: "",
    },
    operations: {
      required: false,
      value: [],
      error: "",
    },
    documents: [],
  });

  const onCountryChange = (e) => {
    setFormData({
      ...formData,
      country: {
        value: e.value,
        error: "",
        required: true,
      },
    });
  };
  const onNameChange = (e) => {
    setFormData({
      ...formData,
      name: {
        value: e.target.value,
        error: "",
        required: true,
      },
    });
  };

  const onCodeChange = (e) => {
    setFormData({
      ...formData,
      code: {
        value: e.target.value,
        error: "",
        required: true,
      },
    });
  };

  const onSubCategoryChange = (e) => {
    setFormData({
      ...formData,
      subCategory: {
        value: e.value,
        error: "",
        required: true,
      },
    });
  };

  const validateForm = (data) => {
    let isFormValid = true;
    if (!data.name.value) {
      isFormValid = false;
      data.name.error = t("LABEL_REQUIRED");
    }
    if (!data.country.value) {
      isFormValid = false;
      data.country.error = t("COUNTRY_REQUIRED");
    }
    if (!data.subCategory.value) {
      isFormValid = false;
      data.subCategory.error = t("SUBCATEGORY_REQUIRED");
    }
    if (!isFormValid) {
      setFormData({ ...data });
    }

    return isFormValid;
  };

  const getFormData = () => {
    const dataModified = new FormData();
    dataModified.append("id", id);
    dataModified.append("name", formData.name.value);
    dataModified.append("productCode", formData.code.value);
    dataModified.append(
      "subCategory",
      JSON.stringify({
        id: formData.subCategory.value,
      })
    );
    dataModified.append(
      "country",
      JSON.stringify({
        id: formData.country.value,
      })
    );
    dataModified.append("isActive", 1);
    dataModified.append("canEvacuate", jobTypes.type1.status);
    dataModified.append("canDelivery", jobTypes.type2.status);
    if (formData.documents.length > 0) {
      for (let i = 0; i < formData.documents.length; i++) {
        dataModified.append(
          `document[${i}][type]`,
          JSON.stringify({
            id: "0",
          })
        );
        dataModified.append(
          `document[${i}][filename]`,
          formData.documents[i].name
        );
        dataModified.append(`document[${i}][file]`, formData.documents[i]);
      }
    }
    if (existingDocuments.length > 0) {
      let i = formData.documents.length > 0 ? formData.documents.length : 0;
      existingDocuments.forEach((document) => {
        dataModified.append(`document[${i}][filename]`, document.fileName);
        dataModified.append(`document[${i}][id]`, document.id);
        dataModified.append(`document[${i}][file]`, document.fileName);
        i += 1;
      });
    }

    return dataModified;
  };
  const onSubmit = () => {
    const isFormValid = validateForm(formData);
    if (isFormValid) {
      const data = getFormData(formData);
      setIsProductCreating(true);
      setCreateProductAPIError("");
      // Create solution
      if (!id && !isEditMode) {
        axios
          .post(`/products`, data, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          })
          .then(() => {
            setIsProductCreating(false);
            updateRefeshStatus();
            onClose();
          })
          .catch((error) => {
            setIsProductCreating(false);
            setCreateProductAPIError(error.response?.data?.message ?? "");
          });
      }
      // Edit
      if (id && isEditMode) {
        axios
          .post(`/products/${id}`, data, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          })
          .then(() => {
            setIsProductCreating(false);
            updateRefeshStatus();
            onClose();
          })
          .catch((error) => {
            setIsProductCreating(false);
            setCreateProductAPIError(error.response?.data?.message ?? "");
          });
      }
    }
  };

  return (
    <Dialog>
      <div>
        <HeaderWrapper>
          <Title>
            {title} {id ? "#" + id : ""}
          </Title>
          <Button
            onClick={onClose}
            styles={{
              backgroundColor: "transparent",
              border: "none",
              height: "auto",
              padding: 0,
              "&:hover": {
                backgroundColor: "transparent",
              },
            }}
          >
            <Image
              src={closeIcon}
              alt="close Product price popup"
              styles={{ width: "auto" }}
            />
          </Button>
        </HeaderWrapper>
        <FormBody>
          <OptionsWrapper>
            <FlexRow>
              <SectionContainer>
                <div>
                  <GridRow column="1fr 1fr">
                    <TextInput
                      id="product-name"
                      type="text"
                      label={t("NAME")}
                      isFloat
                      value={String(formData.name.value)}
                      onChange={(e) => onNameChange(e)}
                      containerStyles={{ marginTop: "21px" }}
                      errorStyles={{ "font-style": "italic" }}
                      labelStyles={
                        formData.name.error !== ""
                          ? labelStylesError
                          : labelStyles
                      }
                      inputStyles={
                        formData.name.error !== ""
                          ? inputStylesError
                          : inputStyles
                      }
                      required
                      error={formData.name.error}
                      placeholder={t("NAME")}
                    />
                    <Dropdown
                      id="product-country"
                      label={t("COUNTRY")}
                      name="country"
                      value={formData.country.value}
                      onChange={onCountryChange}
                      defaultOption={t("COUNTRY")}
                      options={countryList}
                      labelStyles={
                        formData.country.error !== ""
                          ? labelStylesError
                          : labelStyles
                      }
                      inputStyles={
                        formData.country.error !== ""
                          ? inputStylesError
                          : inputStyles
                      }
                      required
                      error={formData.country.error}
                    />
                  </GridRow>
                  <GridRow column="1fr 1fr">
                    <Dropdown
                      id="product-sub_category"
                      label={t("SUB_CATEGORY")}
                      name="sub_category"
                      value={formData.subCategory.value}
                      onChange={onSubCategoryChange}
                      defaultOption={t("SUB_CATEGORY")}
                      options={subCategoryList}
                      labelStyles={
                        formData.subCategory.error !== ""
                          ? labelStylesError
                          : labelStyles
                      }
                      inputStyles={
                        formData.subCategory.error !== ""
                          ? inputStylesError
                          : inputStyles
                      }
                      required
                      error={formData.subCategory.error}
                    />
                    <OperationWrapper>
                      <GridRow column="1fr">
                        <ContainerLabel>{t("OPERATIONS")}</ContainerLabel>
                      </GridRow>
                      <GridRow column="1fr 1fr">
                        <CustomCheckBox
                          id="1"
                          labelText={t("REMOVAL")}
                          dataId="1"
                          onChange={(e) => {
                            onRoleChange(e);
                          }}
                          checked={jobTypes.type1.status}
                        />
                        <CustomCheckBox
                          id="2"
                          labelText={t("SUPPLY")}
                          dataId="2"
                          onChange={(e) => {
                            onRoleChange(e);
                          }}
                          checked={jobTypes.type2.status}
                        />
                      </GridRow>
                    </OperationWrapper>
                  </GridRow>
                  <GridRow column="1fr 1fr">
                    <TextInput
                      id="product-code"
                      type="text"
                      label={t("PRODUCT_CODE")}
                      isFloat
                      value={String(formData.code.value)}
                      onChange={(e) => onCodeChange(e)}
                      containerStyles={{ marginTop: "21px" }}
                      errorStyles={{ "font-style": "italic" }}
                      labelStyles={
                        formData.code.error !== ""
                          ? labelStylesError
                          : labelStyles
                      }
                      inputStyles={
                        formData.code.error !== ""
                          ? inputStylesError
                          : inputStyles
                      }
                      error={formData.code.error}
                      placeholder={t("PRODUCT_CODE")}
                    />
                  </GridRow>
                  <DocumentUpload
                    isEditMode={isEditMode}
                    setFormData={setFormData}
                    formData={formData}
                    selectedProduct={selectedProduct}
                    existingDocuments={existingDocuments}
                    setExistingDocuments={setExistingDocuments}
                  />
                </div>
              </SectionContainer>
            </FlexRow>
          </OptionsWrapper>
        </FormBody>

        <ButtonWrapper>
          {createProductAPIError && <div>{createProductAPIError}</div>}
          <Button
            styles={{
              color: "white",
              background: "#1BACE4",
              border: "1px solid #1BACE4",
              marginRight: "0.32rem",
              "&:hover": {
                backgroundColor: "#1589b6",
              },
            }}
            onClick={onSubmit}
          >
            <>
              {isProductCreating && <ClipLoader size={15} color="#fff" />}
              {!isProductCreating && t("SUBMIT")}
            </>
          </Button>

          <Button
            styles={{
              color: "#333",
              background: "#fff",
              border: "1px solid #ccc",
              "&:hover": {
                backgroundColor: "#ccc",
              },
            }}
            onClick={onClose}
          >
            {t("CLOSE")}
          </Button>
        </ButtonWrapper>
      </div>
    </Dialog>
  );
};

AddEditProduct.propTypes = {
  t: PropTypes.func.isRequired,
  id: PropTypes.number,
  title: PropTypes.string.isRequired,
  isEditMode: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  updateRefeshStatus: PropTypes.func.isRequired,
};

AddEditProduct.defaultProps = {
  id: null,
  isEditMode: false,
};
export default withTranslation()(AddEditProduct);
